import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FleetViewDevice, FleetViewFilterKeys, FleetViewSort } from './types';
import { DeviceDeploymentStatus } from '../devices/types';
import { AddressEntity } from '../addresses/types';

interface FleetViewState {
  selectedSerialNumber: string | null;
  selectedCompany: string | null;
  devices: FleetViewDevice[];
  searchQuery?: string;
  filter: { [K in keyof typeof FleetViewFilterKeys]: boolean };
  sort: FleetViewSort;
  selectedDeploymentStatus: DeviceDeploymentStatus | null;
  selectedAddress: AddressEntity | null;
}

const initialState: FleetViewState = {
  devices: [],
  selectedSerialNumber: null,
  filter: Object.fromEntries(
    Object.keys(FleetViewFilterKeys).map((key) => [key, true])
  ) as { [K in keyof typeof FleetViewFilterKeys]: boolean },
  sort: FleetViewSort.UPDATED_DESC,
  selectedCompany: null,
  selectedDeploymentStatus: null,
  selectedAddress: null,
};

const fleetViewSlice = createSlice({
  name: 'fleetView',
  initialState,
  reducers: {
    setSelectedSerialNumber(state, action: PayloadAction<string | null>) {
      state.selectedSerialNumber = action.payload;
    },
    setSelectedCompany(state, action: PayloadAction<string | null>) {
      state.selectedCompany = action.payload;
    },

    setDevices(state, action: PayloadAction<FleetViewDevice[]>) {
      state.devices = action.payload;
    },
    setSearchQuery(state, action: PayloadAction<string | undefined>) {
      state.searchQuery = action.payload;
    },
    toggleFilter(
      state,
      action: PayloadAction<{ filter: keyof typeof FleetViewFilterKeys }>
    ) {
      const newFilter = {
        ...state.filter,
        [action.payload.filter]: !state.filter[action.payload.filter],
      };
      state.filter = newFilter;
    },
    setSort(state, action: PayloadAction<FleetViewSort>) {
      state.sort = action.payload;
    },
    setSelectedDeploymentStatus(
      state,
      action: PayloadAction<DeviceDeploymentStatus | null>
    ) {
      state.selectedDeploymentStatus = action.payload;
    },
    setSelectedAddress: (state, { payload }: PayloadAction<AddressEntity>) => {
      state.selectedAddress = payload;
    },
    clearSelectedAddress: (state) => {
      state.selectedAddress = null;
    },
  },
});

export const {
  setSelectedSerialNumber,
  setSelectedCompany,
  setDevices,
  setSearchQuery,
  toggleFilter,
  setSort,
  setSelectedDeploymentStatus,
  setSelectedAddress,
  clearSelectedAddress,
} = fleetViewSlice.actions;

export default fleetViewSlice.reducer;
